import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster, toast } from "@/components/ui/sonner";
import { AppShell, defaultTabForRole, tabKeysForRole } from "@/components/AppShell";
import { AuthScreen } from "@/components/auth/AuthScreen";
import { InstallBanner } from "@/components/InstallPrompt";
import { LegalPage } from "@/components/legal/LegalPage";
import { NotificationPrompt } from "@/components/NotificationPrompt";
import { PhoneGate } from "@/components/PhoneGate";
import ReferralLanding from "@/components/ReferralLanding";
import { RoleRouter } from "@/components/RoleRouter";
import { SetPasswordGate } from "@/components/SetPasswordGate";
import { useAuth } from "@/hooks/useAuth";
import { SiteProvider } from "@/contexts/SiteContext";
import { applyUpdate, registerServiceWorker } from "@/lib/pwa";

const Splash = () => (
  <div className="grid min-h-screen place-items-center bg-brand-canvas text-brand-forest">
    <div className="flex flex-col items-center gap-5">
      <div className="flex items-center gap-3">
        <img src="/logo-mark.png" alt="Greentek Alliance" className="h-14 w-14" />
        <div className="text-left leading-[1.08]">
          <span className="block text-[24px] font-bold uppercase tracking-[0.08em] text-brand-ink">Greentek</span>
          <span className="block text-[24px] font-bold uppercase tracking-[0.08em] text-brand-gold">Alliance</span>
        </div>
      </div>
      <Loader2 className="animate-spin text-brand-muted" size={22} />
    </div>
  </div>
);

function App() {
  const {
    session,
    sessionEpoch,
    authReady,
    role,
    phoneDeferred,
    markPhoneSet,
    markPasswordSet,
    deferPhone,
    login,
    sendOtpCode,
    verifyOtpCode,
    googleLogin,
    apply,
    logout,
    startDevRole,
    switchRole,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const referralMatch = location.pathname.match(/^\/r\/([^/]+)\/?$/i);
  const referralCode = referralMatch?.[1] || null;

  useEffect(() => {
    registerServiceWorker({
      onUpdate: (registration) =>
        toast("Update available", {
          id: "sw-update",
          duration: Infinity,
          action: {
            label: "Reload",
            onClick: () => applyUpdate(registration),
          },
        }),
    });
  }, []);

  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const resetToken =
    typeof window !== "undefined" && window.location.pathname === "/reset-password"
      ? params?.get("token")
      : null;

  // In-app legal reader, shown as an overlay on the /legal/* routes (works signed
  // out on the auth screen and signed in over the app shell).
  const legalDoc =
    location.pathname === "/legal/privacy"
      ? "privacy"
      : location.pathname === "/legal/terms"
        ? "terms"
        : null;
  const legalOverlay = legalDoc ? <LegalPage doc={legalDoc} /> : null;

  // /register deep-links straight to the sign-up form; everything else on the
  // signed-out auth screen is the login view. Match case- and trailing-slash-
  // insensitively so shared links like /Register or /register/ still resolve.
  // While a legal overlay is open the pathname is /legal/*, so freeze the
  // underlying view (via a ref) instead of flipping it to "login". Otherwise
  // the AuthScreen would swap out and wipe a half-filled registration form
  // sitting behind the overlay.
  const authViewRef = useRef("login");
  if (!legalDoc) {
    const path = location.pathname.replace(/\/+$/, "").toLowerCase();
    authViewRef.current = path === "/register" ? "apply" : "login";
  }
  const authView = authViewRef.current;

  const preLoginPathRef = useRef(null);
  if (authReady && !session && !resetToken && !legalDoc && !referralCode) {
    const path = location.pathname.replace(/\/+$/, "").toLowerCase() || "/";
    preLoginPathRef.current = ["/", "/register", "/reset-password"].includes(path)
      ? null
      : path;
  }

  const handledSessionEpochRef = useRef(sessionEpoch);

  // Normalize the URL to a valid tab for the current role (also handles "/",
  // deep links, and dev role switches). Skip while a legal page is open so the
  // overlay's route isn't rewritten out from under it.
  useEffect(() => {
    if (authReady && session && !resetToken && !legalDoc && !referralCode) {
      const roleTabs = tabKeysForRole(role);
      const newSessionStarted = sessionEpoch !== handledSessionEpochRef.current;
      if (newSessionStarted) {
        handledSessionEpochRef.current = sessionEpoch;
        const preLoginTab = preLoginPathRef.current?.replace(/^\//, "");
        preLoginPathRef.current = null;
        const target = roleTabs.includes(preLoginTab)
          ? preLoginTab
          : defaultTabForRole(role);
        if (location.pathname !== `/${target}`) {
          navigate(`/${target}`, { replace: true });
        }
        return;
      }

      const tab = location.pathname.replace(/^\//, "");
      if (!roleTabs.includes(tab)) {
        navigate(`/${defaultTabForRole(role)}`, { replace: true });
      }
    }
  }, [authReady, session, sessionEpoch, resetToken, legalDoc, referralCode, role, location.pathname, navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/", { replace: true });
  }, [logout, navigate]);

  let content;

  if (referralCode) {
    content = <ReferralLanding code={referralCode} session={session} />;
  } else if (!authReady) {
    content = <Splash />;
  } else if (!session || resetToken) {
    content = (
      <>
        <AuthScreen
          initialView={resetToken ? "reset" : authView}
          resetToken={resetToken}
          onLogin={login}
          onSendOtp={sendOtpCode}
          onVerifyOtp={verifyOtpCode}
          onGoogle={googleLogin}
          onApply={apply}
          onStartDevRole={startDevRole}
        />
        <InstallBanner />
        {legalOverlay}
      </>
    );
  } else {
    content = (
      <>
        <SiteProvider
          key={`${role}:${session.memberId ?? "no-member"}`}
          memberId={session.memberId}
          enabled={role !== "admin"}
        >
          <AppShell role={role} setRole={switchRole} onLogout={handleLogout}>
            <RoleRouter role={role} memberId={session.memberId} accessReady={authReady} />
          </AppShell>
        </SiteProvider>
        <InstallBanner aboveNav />
        {role !== "admin" && session.needsPhone && !phoneDeferred ? (
          <PhoneGate onDone={markPhoneSet} onDefer={deferPhone} onLogout={handleLogout} />
        ) : role !== "admin" && session.needsPassword ? (
          <SetPasswordGate onDone={markPasswordSet} />
        ) : (
          <NotificationPrompt />
        )}
        {legalOverlay}
      </>
    );
  }

  return (
    <>
      {content}
      <Toaster richColors position="top-center" />
    </>
  );
}

export default App;
